import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Link } from "react-router-dom";
import { MailX, CheckCircle2, ArrowLeft } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import ResponsiveSection from "@/components/layout/ResponsiveSection";
import PageBreadcrumbs from "@/components/PageBreadcrumbs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Helmet } from "react-helmet-async";

const unsubscribeSchema = z.object({
  email: z.string().email({ message: "Bitte geben Sie eine gültige E-Mail-Adresse ein" }),
  reason: z.string().optional(),
});

type UnsubscribeFormData = z.infer<typeof unsubscribeSchema>;

const NewsletterAbmelden = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { toast } = useToast();

  const form = useForm<UnsubscribeFormData>({
    resolver: zodResolver(unsubscribeSchema),
    defaultValues: {
      email: "",
      reason: "",
    },
  });

  const onSubmit = async (data: UnsubscribeFormData) => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke("newsletter-unsubscribe", {
        body: {
          email: data.email,
          reason: data.reason || null,
        },
      });

      if (error) throw error;

      setIsSuccess(true);
      toast({
        title: "Erfolgreich abgemeldet",
        description: "Sie wurden vom Newsletter abgemeldet.",
      });
      form.reset();
    } catch (error: any) {
      console.error("Newsletter unsubscribe error:", error);
      toast({
        variant: "destructive",
        title: "Fehler",
        description: error.message || "Abmeldung fehlgeschlagen. Bitte versuchen Sie es später erneut.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Newsletter Abmeldung - praxis-versichert.de</title>
        <meta name="description" content="Melden Sie sich vom Newsletter ab. Wir respektieren Ihre Entscheidung und löschen Sie umgehend aus unserem Verteiler." />
      </Helmet>

      <>
        <Navigation />
        <PageBreadcrumbs items={[
          { label: "Home", path: "/" },
          { label: "Newsletter", path: "/newsletter" },
          { label: "Abmelden" }
        ]} />
        
        {/* Wellenförmiger Hintergrund */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_120%_100%_at_50%_20%,hsl(var(--accent)/0.08)_0%,hsl(var(--accent)/0.04)_30%,transparent_60%)] opacity-60"></div>
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_120%_100%_at_50%_60%,hsl(var(--accent)/0.10)_0%,hsl(var(--accent)/0.05)_35%,transparent_65%)] opacity-70"></div>
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_100%_80%_at_50%_90%,hsl(var(--accent)/0.06)_0%,hsl(var(--accent)/0.02)_40%,hsl(var(--background))_75%)]"></div>
        </div>

        <div className="min-h-screen flex flex-col relative z-10">
        
        {/* Hero Section */}
        <ResponsiveSection variant="accent" spacing="lg">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-6">
              <MailX className="w-8 h-8 text-muted-foreground" />
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Newsletter abmelden
            </h1>
            <p className="text-lg text-muted-foreground">
              Schade, dass Sie gehen möchten. Sie können sich jederzeit wieder anmelden.
            </p>
          </div>
        </ResponsiveSection>

        {/* Unsubscribe Information */}
        <ResponsiveSection spacing="lg">
          <div className="max-w-2xl mx-auto">
            <div className="bg-card/50 border border-border rounded-lg p-6 mb-8">
              <h2 className="text-xl font-semibold mb-3">Hinweis zur Abmeldung</h2>
              <p className="text-muted-foreground mb-4">
                Falls Sie diese Seite erreicht haben, weil Sie eine E-Mail von uns erhalten haben, obwohl Sie sich nicht angemeldet haben:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                <li>Wir entschuldigen uns für die Unannehmlichkeiten</li>
                <li>Ihre E-Mail-Adresse wird nach der Abmeldung sofort aus unserem System entfernt</li>
                <li>Sie werden keine weiteren E-Mails von uns erhalten</li>
                <li>Wir nehmen Datenschutz sehr ernst und respektieren Ihre Entscheidung</li>
              </ul>
            </div>

            {/* Unsubscribe Form */}
            {!isSuccess ? (
              <div className="bg-card border border-border rounded-lg p-6 sm:p-8">
                <h3 className="text-xl font-semibold mb-6">
                  Vom Newsletter abmelden
                </h3>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="email">
                      E-Mail-Adresse <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="ihre.email@beispiel.de"
                      {...form.register("email")}
                      disabled={isSubmitting}
                    />
                    {form.formState.errors.email && (
                      <p className="text-sm text-destructive">{form.formState.errors.email.message}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Geben Sie die E-Mail-Adresse ein, mit der Sie angemeldet sind
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reason">Grund für die Abmeldung (optional)</Label>
                    <Textarea
                      id="reason"
                      placeholder="Ihr Feedback hilft uns, unseren Service zu verbessern..."
                      rows={4}
                      {...form.register("reason")}
                      disabled={isSubmitting}
                    />
                    <p className="text-xs text-muted-foreground">
                      Ihr Feedback ist freiwillig und hilft uns, unseren Service zu verbessern
                    </p>
                  </div>

                  <Button type="submit" className="w-full" size="lg" disabled={isSubmitting} variant="destructive">
                    {isSubmitting ? "Wird abgemeldet..." : "Vom Newsletter abmelden"}
                  </Button>

                  <div className="text-center">
                    <Link to="/newsletter" className="text-sm text-primary hover:underline inline-flex items-center gap-2">
                      <ArrowLeft className="w-4 h-4" />
                      Doch lieber anmelden?
                    </Link>
                  </div>
                </form>
              </div>
            ) : (
              <div className="bg-card border border-border rounded-lg p-8 text-center">
                <CheckCircle2 className="w-16 h-16 text-primary mx-auto mb-4" />
                <h3 className="text-2xl font-bold mb-2">Erfolgreich abgemeldet</h3>
                <p className="text-muted-foreground mb-6">
                  Sie wurden erfolgreich vom Newsletter abgemeldet. Sie erhalten keine weiteren E-Mails von uns.
                </p>
                <p className="text-sm text-muted-foreground mb-6">
                  Sollten Sie es sich anders überlegen, können Sie sich jederzeit wieder anmelden.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button asChild variant="outline">
                    <Link to="/">Zur Startseite</Link>
                  </Button>
                  <Button asChild>
                    <Link to="/newsletter">Wieder anmelden</Link>
                  </Button>
                </div>
              </div>
            )}
          </div>
        </ResponsiveSection>

        <Footer />
        </div>
      </>
    </>
  );
};

export default NewsletterAbmelden;